import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTenantUserInfo } from '../../context/userInfoProvider';
import { getAppointments } from '../../components/TenantApp/getAppointments';
import { getAvailableSlots, assignDriver, SlotKey, SLOT_TIMES } from '../../utils/driverAvailability';

type LocationState = {
  selected: string[] | string;
  type: 'DELIVERY' | 'PICKUP' | string;
  id?: string[];
  needHelp?: string;
  needPack?: string;
  moverOption?: string;
};

type Appointment = {
  facilityID: string;
  date: string;
  time: string;
  driver: string;
  [key: string]: any;
};

type SlotInfo = { slotKey: SlotKey; time: string; paid: boolean };

function getPaidPricing(planSize: string): string {
  return planSize?.includes('10') ? '$120 flat + $2/min' : '$60 flat + $1/min';
}

const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#F5F5FA',
  fontSize: 14,
};

const pageContainer: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const shellCard: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background: 'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
};

const headerBlock: CSSProperties = { marginBottom: 16 };
const titleStyle: CSSProperties = { fontSize: 22, fontWeight: 600 };

const labelStyle: CSSProperties = {
  ...baseFont, fontSize: 13, fontWeight: 500, marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid #CCCCCC', fontSize: 14,
  fontFamily: baseFont.fontFamily, boxSizing: 'border-box', color: '#111827',
};

const hintTextStyle: CSSProperties = {
  ...baseFont, fontSize: 12, color: '#D0D4FF', opacity: 0.9, marginTop: 8,
};

const chipsRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 };

const chip: CSSProperties = {
  padding: '8px 12px', borderRadius: 999, border: '1px solid #7B1FA2',
  backgroundColor: 'transparent', color: '#F5F5FA', fontSize: 13, cursor: 'pointer',
};

const chipSelected: CSSProperties = { backgroundColor: '#7B1FA2', borderColor: '#C5A9FF' };

const errorTextStyle: CSSProperties = {
  ...baseFont, fontSize: 12, color: '#f97373', marginTop: 6,
};

const footer: CSSProperties = { marginTop: 24, display: 'flex', justifyContent: 'flex-end' };

const primaryButton: CSSProperties = {
  padding: '10px 16px', borderRadius: 6, border: 'none',
  backgroundColor: '#7B1FA2', color: '#FFF', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const TenantScheduleAppointmentPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;
  const { facilityID, size, drivers } = useTenantUserInfo();
  const paidPricingLabel = getPaidPricing(size);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState<SlotInfo[]>([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [error, setError] = useState<string | null>(null);

  const minDate = useMemo(() => addDays(2), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingAppointments(true);
        const data = await getAppointments();
        const parsed = Array.isArray(data)
          ? data
          : JSON.parse((data as any).body ?? '[]');
        const facilityAppointments = parsed.filter(
          (appt: Appointment) => appt.facilityID === facilityID
        );
        setAppointments(facilityAppointments);
      } catch (err) {
        console.error('Failed to fetch appointments:', err);
      } finally {
        setLoadingAppointments(false);
      }
    };
    fetchData();
  }, [facilityID]);

  if (!state) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <div style={{ ...baseFont }}>
            <h2>Missing order details</h2>
            <p>Please start again from &quot;Schedule appointment&quot;.</p>
          </div>
        </div>
      </div>
    );
  }

  const recalcSlotsForDate = (dateStr: string) => {
    setSelectedSlot('');
    if (!dateStr) { setAvailableSlots([]); return; }

    const available = getAvailableSlots(drivers, appointments, dateStr);
    const slots: SlotInfo[] = (Object.entries(available) as [SlotKey, { isPaid: boolean }][])
      .map(([slotKey, info]) => ({
        slotKey,
        time: SLOT_TIMES[slotKey],
        paid: info.isPaid,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));

    setAvailableSlots(slots);
  };

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    recalcSlotsForDate(value);
    setError(null);
  };

  const handleNext = () => {
    if (!selectedDate) { setError('Please select a date.'); return; }
    if (!selectedSlot) { setError('Please select a time window.'); return; }

    const slot = availableSlots.find(s => s.time === selectedSlot);
    const slotPaid = slot?.paid ?? false;
    const slotKey = slot?.slotKey ?? ('morning' as SlotKey);
    const assigned = assignDriver(drivers, appointments, selectedDate, slotKey);

    history.push('/tenant/neworder/address', {
      ...state,
      dateSelected: selectedDate,
      timeSelected: selectedSlot,
      slotPaid,
      driverAssigned: assigned?.name ?? '',
      paidPricingLabel: slotPaid ? paidPricingLabel : undefined,
    });
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerBlock}>
          <h1 style={titleStyle}>Choose a date and time</h1>
          <div style={hintTextStyle}>
            We&apos;ll confirm your appointment once a driver is assigned.
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Pickup / delivery date</label>
          <input
            type="date"
            min={minDate}
            value={selectedDate}
            onChange={e => handleDateChange(e.target.value)}
            style={inputStyle}
          />
          <div style={hintTextStyle}>
            Slots shown are based on driver availability. Free slots are included in your plan — paid slots carry an additional moving fee.
          </div>
        </div>

        <div>
          <label style={labelStyle}>Arrival window</label>
          <div style={chipsRow}>
            {loadingAppointments && (
              <div style={hintTextStyle}>Checking availability…</div>
            )}
            {!loadingAppointments && !selectedDate && (
              <div style={hintTextStyle}>Pick a date first to see available windows.</div>
            )}
            {!loadingAppointments && selectedDate && availableSlots.length === 0 && (
              <div style={hintTextStyle}>No availability on this date. Please choose another day.</div>
            )}
            {!loadingAppointments && selectedDate && availableSlots.map(slot => (
              <button
                key={slot.time}
                type="button"
                onClick={() => { setSelectedSlot(slot.time); setError(null); }}
                style={{
                  ...chip,
                  ...(selectedSlot === slot.time ? chipSelected : {}),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <span>{slot.time}</span>
                <span style={{ fontSize: 11, opacity: 0.8 }}>
                  {slot.paid ? paidPricingLabel : 'Free'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {error && <div style={errorTextStyle}>{error}</div>}

        <div style={footer}>
          <button type="button" style={primaryButton} onClick={handleNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantScheduleAppointmentPage;
